/**
 * SKOP Sentiment Details
 *
 * Expanded view of social sentiment with mentions and trends.
 */

import {
  type TokenSentiment,
  getSentimentColor,
  getSentimentLabel,
  getTrendColor,
  getSourceLabel,
  formatMentionTime,
} from '../lib/sentiment';

interface SentimentDetailsProps {
  sentiment: TokenSentiment;
  onClose: () => void;
}

export function SentimentDetails({ sentiment, onClose }: SentimentDetailsProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Social Sentiment Analysis
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              background: 'none',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '18px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        {/* Score Summary */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '16px',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '20px',
        }}>
          <div style={{
            fontSize: '36px',
            fontWeight: 700,
            color: getSentimentColor(sentiment.overallScore),
          }}>
            {sentiment.overallScore > 0 ? '+' : ''}{sentiment.overallScore}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>
              {getSentimentLabel(sentiment.overallScore)}
            </div>
            <div style={{
              fontSize: '12px',
              color: getTrendColor(sentiment.trend),
            }}>
              Trend: {sentiment.trend.charAt(0).toUpperCase() + sentiment.trend.slice(1)}
            </div>
          </div>
        </div>

        {/* 7-Day Trend Chart */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '12px',
            fontWeight: 600,
          }}>
            7-DAY SENTIMENT TREND
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'end',
            gap: '4px',
            height: '80px',
            padding: '8px',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
          }}>
            {sentiment.historicalScores.map((point, i) => {
              const normalizedHeight = ((point.score + 100) / 200) * 100;
              const date = new Date(point.timestamp);
              const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${normalizedHeight}%`,
                      minHeight: '4px',
                      background: getSentimentColor(point.score),
                      borderRadius: '2px',
                      transition: 'height 0.3s ease',
                    }}
                    title={`${point.score > 0 ? '+' : ''}${point.score}`}
                  />
                  <span style={{
                    fontSize: '9px',
                    color: 'var(--text-tertiary)',
                  }}>
                    {dayLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Hashtags */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px',
            fontWeight: 600,
          }}>
            TRENDING HASHTAGS
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {sentiment.topHashtags.map((tag) => (
              <span
                key={tag}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  background: 'var(--accent-muted)',
                  color: 'var(--accent)',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 500,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Recent Mentions */}
        <div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '12px',
            fontWeight: 600,
          }}>
            RECENT MENTIONS
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {sentiment.recentMentions.slice(0, 5).map((mention) => (
              <div
                key={mention.id}
                style={{
                  padding: '12px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  borderLeft: `3px solid ${getSentimentColor(mention.sentiment)}`,
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      padding: '2px 6px',
                      fontSize: '10px',
                      fontWeight: 600,
                      background: 'var(--surface-secondary)',
                      borderRadius: 'var(--radius-sm)',
                    }}>
                      {getSourceLabel(mention.source)}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>
                      {mention.author}
                    </span>
                    {mention.authorHandle && (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                        {mention.authorHandle}
                      </span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                  }}>
                    {formatMentionTime(mention.timestamp)}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  marginBottom: '8px',
                }}>
                  {mention.text}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '11px',
                  color: 'var(--text-tertiary)',
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#ff6b6b' }}>
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    {mention.engagement.likes}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 1l4 4-4 4"/>
                      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                      <path d="M7 23l-4-4 4-4"/>
                      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                    </svg>
                    {mention.engagement.retweets}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    {mention.engagement.replies}
                  </span>
                  {mention.url && (
                    <a
                      href={mention.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--accent)',
                        textDecoration: 'none',
                        marginLeft: 'auto',
                      }}
                    >
                      View →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="btn btn-secondary"
          style={{ width: '100%', marginTop: '20px' }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
